import React, { useState, useMemo, useRef } from 'react';
import { uid, formatMontant } from '../utils/helpers';
import { uploadToStorage, createSignedUrl } from '../supabaseClient';

const CATEGORIES = ['Déplacement','Fournitures','Formation','Communication','Réunion','Juridique','Autre'];

const EMPTY_FORM = { date: '', montant: '', motif: '', categorie: 'Autre', justificatif: null };

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve({ name: file.name, type: file.type, data: e.target.result });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR');
}

export default function SuiviDepenses({ depenses = [], budgetDepenses = {}, selectedYear, saveDepense, deleteDepense, saveBudget, currentRole }) {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [err, setErr] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [editBudget, setEditBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileRef = useRef();
  const isTresorier = currentRole === 'tresorier';
  const isReadOnly  = !isTresorier;

  const yearDepenses = useMemo(() =>
    depenses.filter(d => d.annee === selectedYear)
      .sort((a, b) => new Date(b.date) - new Date(a.date)),
    [depenses, selectedYear]
  );

  const budget = budgetDepenses[selectedYear] || 0;
  const totalDepense = yearDepenses.reduce((s, d) => s + (d.montant || 0), 0);
  const restant = budget - totalDepense;
  const pct = budget > 0 ? Math.min(100, (totalDepense / budget) * 100) : 0;
  const barColor = pct >= 90 ? '#b91c1c' : pct >= 70 ? '#c2410c' : '#16a34a';

  const f = k => v => setForm(prev => ({ ...prev, [k]: v }));

  const handleFile = async e => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErr('Fichier trop grand (max 5 Mo)'); return; }
    const b64 = await fileToBase64(file);
    setForm(prev => ({ ...prev, justificatif: { ...b64, size: file.size } }));
    setUploadFailed(false);
    setErr('');
  };

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, date: new Date().toISOString().slice(0, 10) });
    setEditId(null);
    setErr('');
    setUploadFailed(false);
    setIsUploading(false);
    setShowModal(true);
  };

  const openEdit = dep => {
    setForm({
      date: dep.date || '',
      montant: String(dep.montant || ''),
      motif: dep.motif || '',
      categorie: dep.categorie || 'Autre',
      justificatif: dep.justificatif || null,
    });
    setEditId(dep.id);
    setErr('');
    setUploadFailed(false);
    setIsUploading(false);
    setShowModal(true);
  };

  // withoutJustificatif=true : l'utilisateur choisit explicitement de sauvegarder sans PJ
  const handleSubmit = async (withoutJustificatif = false) => {
    if (!form.date) { setErr('Date obligatoire'); return; }
    if (!form.montant || isNaN(Number(form.montant)) || Number(form.montant) <= 0) { setErr('Montant invalide'); return; }
    if (!form.motif.trim()) { setErr('Motif obligatoire'); return; }
    const depId = editId || uid();
    let justificatif = withoutJustificatif ? null : form.justificatif;
    // Upload vers Supabase Storage si le justificatif contient du base64
    // (nouveau fichier sélectionné OU ancienne dépense non encore migrée vers Storage)
    if (justificatif?.data) {
      setIsUploading(true);
      setUploadFailed(false);
      setErr('');
      const path = `depenses/${depId}/${justificatif.name}`;
      const storedPath = await uploadToStorage(path, justificatif.data, justificatif.type);
      setIsUploading(false);
      if (!storedPath) {
        // Échec upload : bloquer la validation, proposer le choix sans PJ
        setUploadFailed(true);
        setErr("Impossible d'envoyer le justificatif. Vérifiez votre connexion ou enregistrez sans pièce jointe.");
        return;
      }
      // Bucket privé : stockage du storagePath uniquement — pas d'URL permanente publique
      justificatif = { name: justificatif.name, type: justificatif.type, storagePath: storedPath, size: justificatif.size };
    }
    const dep = {
      id: depId,
      annee: selectedYear,
      date: form.date,
      montant: parseFloat(form.montant),
      motif: form.motif.trim(),
      categorie: form.categorie,
      justificatif,
      createdAt: editId ? undefined : new Date().toISOString(),
    };
    saveDepense(dep);
    setShowModal(false);
    setForm(EMPTY_FORM);
    setEditId(null);
    setUploadFailed(false);
  };

  const handleBudgetSave = () => {
    const v = parseFloat(budgetInput);
    if (isNaN(v) || v < 0) return;
    saveBudget(selectedYear, v);
    setEditBudget(false);
  };

  const startBudgetEdit = () => {
    setBudgetInput(String(budget || ''));
    setEditBudget(true);
  };

  const handleDelete = id => {
    deleteDepense(id);
    setConfirmDelete(null);
  };

  // Ouvre un justificatif en gérant les 4 cas de compatibilité :
  // 1. base64 local (ancien format) → affichage direct
  // 2. storagePath (bucket privé) → signed URL générée à la volée (1h)
  // 3. url publique sans storagePath (transitoire) → extraction du path + signed URL avec fallback
  // 4. fallback direct (url telle quelle)
  const openJustificatif = async (jus) => {
    if (!jus) return;
    // 1. Ancien format base64 — affichage direct depuis la mémoire locale
    if (jus.data) { setPreviewDoc(jus); return; }
    // 2. storagePath présent → signed URL (bucket privé, 1h)
    if (jus.storagePath) {
      setPreviewDoc({ ...jus, loading: true });
      const signedUrl = await createSignedUrl(jus.storagePath);
      setPreviewDoc(signedUrl ? { ...jus, url: signedUrl } : { ...jus, error: true });
      return;
    }
    // 3. Compatibilité transitoire : ancienne dépense avec url publique Supabase sans storagePath
    //    Tente d'extraire le storagePath depuis l'URL publique pour générer une signed URL
    if (jus.url?.includes('/object/public/documents/')) {
      const extractedPath = jus.url.split('/object/public/documents/')[1]?.split('?')[0];
      if (extractedPath) {
        setPreviewDoc({ ...jus, loading: true });
        const signedUrl = await createSignedUrl(extractedPath);
        // Signed URL si bucket privé ; fallback vers url publique si bucket encore public
        setPreviewDoc({ ...jus, url: signedUrl || jus.url });
        return;
      }
    }
    // 4. Fallback : afficher tel quel (url ou data)
    setPreviewDoc(jus);
  };

  const catColor = cat => {
    const map = {
      Déplacement: '#1d4ed8', Fournitures: '#0369a1', Formation: '#7e22ce',
      Communication: '#c2410c', Réunion: '#166534', Juridique: '#b45309', Autre: '#6b7280',
    };
    return map[cat] || '#6b7280';
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title">💰 Suivi des dépenses</div>
          <div className="page-subtitle">Année {selectedYear} · {yearDepenses.length} dépense{yearDepenses.length !== 1 ? 's' : ''}</div>
        </div>
        {isTresorier && (
          <button className="btn btn-primary" onClick={openCreate}>+ Nouvelle dépense</button>
        )}
      </div>

      {isReadOnly && (
        <div className="alert alert-info mb-4" style={{ fontSize: 12, padding: '8px 14px' }}>
          <span>🔒</span>
          <div>Vue en lecture seule — vous pouvez consulter les dépenses mais pas les modifier.</div>
        </div>
      )}

      {/* Budget + Solde */}
      <div className="grid-3 mb-4" style={{ gap: 14 }}>
        {/* Budget */}
        <div className="stat-card" style={{ borderTopColor: '#1d4ed8' }}>
          <div className="stat-label">Budget annuel {selectedYear}</div>
          {editBudget ? (
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <input
                className="form-control"
                type="number"
                value={budgetInput}
                onChange={e => setBudgetInput(e.target.value)}
                placeholder="0.00"
                style={{ fontSize: 18, fontWeight: 700, padding: '4px 8px' }}
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') handleBudgetSave(); if (e.key === 'Escape') setEditBudget(false); }}
              />
              <button className="btn btn-success btn-sm" onClick={handleBudgetSave}>✓</button>
              <button className="btn btn-outline btn-sm" onClick={() => setEditBudget(false)}>✕</button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
              <span className="stat-value" style={{ color: '#1d4ed8' }}>{formatMontant(budget)}</span>
              {isTresorier && (
                <button className="btn-icon" style={{ fontSize: 13 }} onClick={startBudgetEdit} title="Modifier le budget">✏️</button>
              )}
            </div>
          )}
          <div className="stat-sub">Budget alloué pour l'année</div>
        </div>

        {/* Dépensé */}
        <div className="stat-card" style={{ borderTopColor: barColor }}>
          <div className="stat-label">Total dépensé</div>
          <div className="stat-value" style={{ color: barColor }}>{formatMontant(totalDepense)}</div>
          <div className="stat-sub">{budget > 0 ? `${pct.toFixed(1)} % du budget utilisé` : 'Budget non défini'}</div>
        </div>

        {/* Restant */}
        <div className="stat-card" style={{ borderTopColor: restant >= 0 ? '#166534' : '#b91c1c' }}>
          <div className="stat-label">Solde restant</div>
          <div className="stat-value" style={{ color: restant >= 0 ? '#166534' : '#b91c1c' }}>
            {restant >= 0 ? '' : '−'}{formatMontant(Math.abs(restant))}
          </div>
          <div className="stat-sub">{restant >= 0 ? 'Disponible' : 'Budget dépassé !'}</div>
        </div>
      </div>

      {/* Progress bar */}
      {budget > 0 && (
        <div className="card mb-4" style={{ padding: '14px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
            <span>Consommé : {formatMontant(totalDepense)}</span>
            <span>Budget : {formatMontant(budget)}</span>
          </div>
          <div className="progress" style={{ height: 12 }}>
            <div className="progress-bar" style={{ width: `${pct}%`, background: barColor, borderRadius: 6 }} />
          </div>
          {pct >= 80 && (
            <div className="alert alert-warning mt-3" style={{ padding: '8px 12px' }}>
              ⚠️ {pct >= 100 ? 'Budget dépassé !' : `Attention : ${pct.toFixed(0)} % du budget consommé`}
            </div>
          )}
        </div>
      )}

      {/* Liste des dépenses */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Dépenses {selectedYear}</div>
          {yearDepenses.length > 0 && (
            <span style={{ fontSize: 12, color: '#6b7280' }}>{yearDepenses.length} enregistrement{yearDepenses.length > 1 ? 's' : ''}</span>
          )}
        </div>

        {yearDepenses.length === 0 ? (
          <div className="empty-state" style={{ padding: '30px 0' }}>
            <div className="empty-state-icon">💸</div>
            <div className="empty-state-text">Aucune dépense enregistrée pour {selectedYear}</div>
            {isTresorier && <button className="btn btn-primary mt-3" onClick={openCreate}>+ Ajouter la première dépense</button>}
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Catégorie</th>
                  <th>Motif</th>
                  <th style={{ textAlign: 'right' }}>Montant</th>
                  <th>Justificatif</th>
                  {isTresorier && <th></th>}
                </tr>
              </thead>
              <tbody>
                {yearDepenses.map(dep => (
                  <tr key={dep.id}>
                    <td style={{ whiteSpace: 'nowrap', color: '#6b7280', fontSize: 13 }}>{formatDate(dep.date)}</td>
                    <td>
                      <span style={{
                        background: catColor(dep.categorie) + '18',
                        color: catColor(dep.categorie),
                        borderRadius: 999, padding: '2px 10px',
                        fontSize: 11, fontWeight: 700,
                      }}>{dep.categorie || 'Autre'}</span>
                    </td>
                    <td style={{ maxWidth: 260 }}>{dep.motif}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', color: '#b91c1c' }}>
                      {formatMontant(dep.montant)}
                    </td>
                    <td>
                      {dep.justificatif ? (
                        <button
                          className="btn btn-outline btn-xs"
                          onClick={() => openJustificatif(dep.justificatif)}
                          style={{ gap: 4 }}
                        >
                          {dep.justificatif.type?.includes('pdf') ? '📄' : '🖼️'} Voir
                        </button>
                      ) : (
                        <span style={{ color: '#bbb', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    {isTresorier && (
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn-icon" style={{ fontSize: 13 }} onClick={() => openEdit(dep)} title="Modifier">✏️</button>
                          <button className="btn-icon" style={{ fontSize: 13, color: '#e57373' }} onClick={() => setConfirmDelete(dep.id)} title="Supprimer">🗑️</button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #e5e7eb' }}>
                  <td colSpan={3} style={{ fontWeight: 700, padding: '8px 10px', fontSize: 13 }}>Total</td>
                  <td style={{ textAlign: 'right', fontWeight: 800, color: '#b91c1c', padding: '8px 10px' }}>{formatMontant(totalDepense)}</td>
                  <td colSpan={isTresorier ? 2 : 1} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Modal nouvelle/modifier dépense */}
      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setUploadFailed(false); setIsUploading(false); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{editId ? '✏️ Modifier la dépense' : '+ Nouvelle dépense'}</div>
              <button className="btn-icon" onClick={() => { setShowModal(false); setUploadFailed(false); setIsUploading(false); }}>✕</button>
            </div>

            {err && <div className="alert alert-danger mb-3">{err}</div>}

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input className="form-control" type="date" value={form.date} onChange={e => f('date')(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Montant (€) *</label>
                <input className="form-control" type="number" step="0.01" min="0" placeholder="0.00"
                  value={form.montant} onChange={e => f('montant')(e.target.value)}
                  onFocus={e => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 350)} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Catégorie</label>
              <select className="form-control" value={form.categorie} onChange={e => f('categorie')(e.target.value)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Motif / Description *</label>
              <textarea className="form-control" rows={3} placeholder="Décrivez la dépense..."
                value={form.motif} onChange={e => f('motif')(e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">Pièce jointe (PDF, image, Word — max 5 Mo)</label>
              <div
                className="upload-zone"
                style={{ padding: 16 }}
                onClick={() => fileRef.current.click()}
                onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('drag'); }}
                onDragLeave={e => e.currentTarget.classList.remove('drag')}
                onDrop={async e => {
                  e.preventDefault(); e.currentTarget.classList.remove('drag');
                  const file = e.dataTransfer.files[0];
                  if (file) {
                    if (file.size > 5 * 1024 * 1024) { setErr('Fichier trop grand (max 5 Mo)'); return; }
                    const b64 = await fileToBase64(file);
                    setForm(prev => ({ ...prev, justificatif: { ...b64, size: file.size } }));
                    setUploadFailed(false);
                    setErr('');
                  }
                }}
              >
                {form.justificatif ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                    <span style={{ fontSize: 20 }}>{form.justificatif.type?.includes('pdf') ? '📄' : '🖼️'}</span>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{form.justificatif.name}</span>
                    <button className="btn btn-ghost btn-xs" onClick={e => { e.stopPropagation(); setForm(prev => ({ ...prev, justificatif: null })); }}>✕</button>
                  </div>
                ) : (
                  <div style={{ color: '#9ca3af', fontSize: 13 }}>
                    <div style={{ fontSize: 28, marginBottom: 4 }}>📎</div>
                    Cliquez ou glissez un fichier ici
                  </div>
                )}
              </div>
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx" style={{ display: 'none' }} onChange={handleFile} />
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => { setShowModal(false); setUploadFailed(false); setIsUploading(false); }}>Annuler</button>
              {/* Bouton de secours : enregistrer sans justificatif après un échec d'upload */}
              {uploadFailed && (
                <button
                  className="btn btn-outline"
                  style={{ color: '#f59e0b', borderColor: '#f59e0b' }}
                  onClick={() => handleSubmit(true)}
                >
                  Enregistrer sans pièce jointe
                </button>
              )}
              <button className="btn btn-primary" onClick={() => handleSubmit()} disabled={isUploading}>
                {isUploading ? '⏳ Envoi du fichier...' : editId ? 'Enregistrer' : '+ Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal prévisualisation document */}
      {previewDoc && (
        <div className="modal-overlay" onClick={() => setPreviewDoc(null)}>
          <div className="modal" style={{ maxWidth: 800 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{previewDoc.type?.includes('pdf') ? '📄' : '🖼️'} {previewDoc.name}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {!previewDoc.loading && !previewDoc.error && (
              <a href={previewDoc.url || previewDoc.data} download={previewDoc.name} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">⬇️ Télécharger</a>
            )}
                <button className="btn-icon" onClick={() => setPreviewDoc(null)}>✕</button>
              </div>
            </div>
            {previewDoc.loading ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 14 }}>⏳ Chargement du document...</div>
            ) : previewDoc.error ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#e57373', fontSize: 14 }}>❌ Impossible de charger ce document. Vérifiez votre connexion.</div>
            ) : previewDoc.type?.includes('pdf') ? (
              <iframe src={previewDoc.url || previewDoc.data} title="Justificatif" style={{ width: '100%', height: 500, border: 'none', borderRadius: 8 }} />
            ) : (
              <img src={previewDoc.url || previewDoc.data} alt="Justificatif" style={{ width: '100%', maxHeight: 500, objectFit: 'contain', borderRadius: 8 }} />
            )}
          </div>
        </div>
      )}

      {/* Confirm delete */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Supprimer cette dépense ?</div>
            </div>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 0 }}>Cette action est irréversible.</p>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setConfirmDelete(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={() => handleDelete(confirmDelete)}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
